import * as React from 'react';
import { connect } from 'react-redux';

import CommonTreeSelect, {
  createGroupedArray,
  getFilteredChildrenAndCount,
  SearchableTreeData,
  TreeData,
} from '../CommonTreeSelect';
import { onlyLettersAndSpaces } from '@/common/const';
import { getCountryNameByCountryCode } from '@/common/CountryCodesMap';
import InputControlProps from '@/common/InputControlProps';
import Service, { City } from '@/lib/App';
import { AppState } from '@/store/types';

export interface CitySearchTreeData extends SearchableTreeData {
  countryCode: string;
}

interface experimentCountryCode {
  experimentCountry: string;
}
interface StateProps {
  experimentCountry: experimentCountryCode;
}
/**
 * Basing on cities array that we have this method maps cities in a format, comfortable
 * for being used in select component (with friendly labels).
 *
 * @see { CacheService, CommonTreeSelect }
 *
 * @return { CitySearchTreeData[] } list of select friendly objects
 */
export const getLabeledCitiesArray = (): CitySearchTreeData[] =>
  Service.cache!.getAllCities()
    // filter out cities belongs countries which Grab doesn't operate
    .filter(({ countryCode }) => Service.cache.getCountriesMap()[countryCode])
    // filter out cities with chars other than letters and spaces
    .filter(({ name }) => onlyLettersAndSpaces.test(name))
    // filter out cities with test in the name
    .filter(({ name }) => !name.toLowerCase().includes('test'))
    .map(({ id, name, countryCode: c }: City): CitySearchTreeData => {
      const value = String(id);
      const label = `${id}: ${name} - ${c}`;

      return {
        // label: <span data-cy={`city_${value}`}>{label}</span>,
        title: <span data-cy={`city_${value}`}>{label}</span>,
        search: `${label} ${Service.cache.getCountriesMap()[c]})`.toLowerCase(),
        countryCode: c,
        key: value,
        value,
      };
    });

type CityTreeSelectProps = InputControlProps & StateProps;

export class CityTreeSelect extends React.Component<CityTreeSelectProps> {
  getValuesForSingleParentCase = (values: string, search: string): string[] => {
    if (Service.cache.getCountriesMap()[values]) {
      return (
        this.getTreeData(search)
          // .find(({ value }: TreeData): boolean => value === values)!
          .find(({ key }: TreeData): boolean => key === values)!
          .children!.map(({ key }: TreeData): string => String(key))
      );
    } else {
      return [values];
    }
  };

  getTreeData = (search: string): TreeData[] => {
    const { experimentCountry } = this.props;
    search = search.toLowerCase();

    let selectedCountryCode = '';
    if (experimentCountry.experimentCountry != '') {
      selectedCountryCode = experimentCountry.experimentCountry;
      search = getCountryNameByCountryCode(selectedCountryCode);
      if (search != undefined) {
        search = search.toLowerCase();
      }
    }

    return createGroupedArray(
      getLabeledCitiesArray(),
      'countryCode',
      (countryCode: string, cities: SearchableTreeData[]): TreeData => {
        const { count, children } = getFilteredChildrenAndCount(cities, search);

        return {
          value: countryCode,
          key: countryCode,
          title: `${Service.cache.getCountriesMap()[countryCode]} [${countryCode}] ${count}`,
          children,
        };
      },
    );
  };

  render() {
    const { getValuesForSingleParentCase, getTreeData } = this;
    let placeholder: string | undefined = this.props.placeholder;
    if (!placeholder) {
      placeholder = `Please select cit${this.props.multiple ? 'ies' : 'y'}`;
    }

    return (
      <CommonTreeSelect
        {...{
          ...this.props,
          allValues: getLabeledCitiesArray(),
          getValuesForSingleParentCase,
          getTreeData,
          placeholder,
        }}
      />
    );
  }
}
function mapStateToProps(state: AppState): StateProps {
  return {
    experimentCountry: state.experimentCountry!,
  };
}

export default connect(mapStateToProps)(CityTreeSelect);
